import React from "react";
import "./CurrentOfferings.scss";
import Table from "../../../atoms/Table/Table";

// TEST: uncomment the following and remove the "offerings" from Props.
// const offerings = require("../../../db/offerings.json");

interface Props {
  offerings: Array<any>;
}

const CurrentOfferings: React.FC<Props> = ({ offerings }) => {
  const getOfferings = (offering: any) => {
    if (offering?.type === "Nutraceutical") {
      const offerings = offering.items.map(
        (item: any) =>
          item.quantity > 0 &&
          "product_name" in item.meta_data &&
          item.meta_data.product_name
      );

      return offerings?.filter(Boolean).join(", ");
    } else if (offering?.type.includes("Devices")) {
      const counts: Record<string, number> = {};
      offering?.items?.forEach((item: any) => {
        const name = item?.meta_data?.device_name;
        if (item?.quantity > 0 && name) {
          counts[name] = (counts[name] || 0) + item.quantity;
        }
      });

      return Object.entries(counts)
        .map(([name, count]) => `${name} - ${count}`)
        .join(", ");
    } else if (offering?.type === "HealthCoach Session") {
      const healthCoaches = offering?.items?.map(
        (item: any) => item?.quantity > 0 && item?.meta_data?.healthCoachType
      );

      return healthCoaches?.filter(Boolean).join(", ");
    } else if (offering?.type === "Diagnostic") {
      const activeItems = (offering?.items || []).filter(
        (item: any) => item?.quantity > 0
      );
      const names = activeItems
        .map((item: any) => item?.meta_data?.labTestName)
        .filter(Boolean);

      // Some plans only carry labTestId, so fall back to the plan's own label.
      if (names.length > 0) return names.join(", ");
      return activeItems.length > 0 ? offering?.label : "";
    } else if (offering?.type === "Doctor Consultation") {
      const doctorConsultations = offering?.items?.flatMap((item: any) => {
        if (!(item?.quantity > 0)) return [];
        const specialties = item?.meta_data?.specialties;
        return Array.isArray(specialties) ? specialties : [];
      });

      return doctorConsultations?.filter(Boolean).join(", ");
    }
  };

  // The API can return repeated offerings as "Devices", "Devices - 1", ...
  // Merge them into one entry per base type so they render comma separated.
  const groupedOfferings = (offerings || [])
    .filter((offering: any) => offering?.type !== "Plan Meta Info")
    .reduce((acc: Array<any>, offering: any) => {
      const baseType = String(offering?.type ?? "")
        .replace(/\s*-\s*\d+$/, "")
        .trim();
      const existing = acc.find((group) => group.type === baseType);
      if (existing) {
        existing.items = [...existing.items, ...(offering?.items || [])];
      } else {
        acc.push({ ...offering, type: baseType, items: [...(offering?.items || [])] });
      }
      return acc;
    }, []);

  const offeringsWrapper = groupedOfferings.map((offering: any) => ({
    name: offering?.type,
    offerings: getOfferings(offering) || "-",
  }));

  const columns = [
    {
      label: "Services",
      accessor: "name",
    },
    {
      label: "Details",
      accessor: "offerings",
    },
  ];

  return (
    <div className="offerings-container">
      <Table columns={columns} data={offeringsWrapper} />
    </div>
  );
};

export default CurrentOfferings;
